/**
 * Die Gliederung des Gewerks Elektro — an EINER Stelle.
 *
 * --- Warum diese Datei überhaupt ------------------------------------------
 *
 * Die Unterseiten des Gewerks hingen als Knopfreihe in der Werkzeugleiste der
 * Baumseite. Acht Knöpfe waren es zuletzt, und jedes Paket legte einen weiteren
 * daneben: Verteiler, Datenpflege, Produktdaten, Meldelinien, Melderbedarf,
 * Beleuchtung, Sicherheitsbeleuchtung, Kabeltrassen. Eine Reihe, die nur wächst,
 * sagt irgendwann nichts mehr — sie zeigt acht gleichrangige Knöpfe für Fragen,
 * die nichts miteinander zu tun haben.
 *
 * Deshalb steht die Gliederung jetzt hier, und zwar NUR hier: Abschnitt und
 * Eintrag. Wer eine Seite ergänzt, trägt sie in diese Liste ein und sonst
 * nirgends. Zwei Listen an zwei Stellen sind die sichere Art, dass eine von
 * beiden vergessen wird — genau so war die MSR-Seite einmal gebaut,
 * ausgerollt und trotzdem unerreichbar.
 *
 * --- Warum die Pfade hier stehen und nicht gebaut werden ------------------
 *
 * Die Pfade sind die BESTEHENDEN Routen, unverändert. Deep Links, Lesezeichen
 * und alles, was jemand irgendwo notiert hat, gelten weiter. Diese Datei ist
 * eine Schicht über den Routen, kein Umbau der Routen.
 */

export interface Elektroeintrag {
  /** Der Pfad UNTER /projekte/:id/ — genau wie in App.tsx. */
  pfad: string;
  name: string;
  /** Wozu die Seite da ist. Erscheint als Titel am Baumeintrag. */
  hinweis: string;
}

export interface Elektroabschnitt {
  name: string;
  eintraege: Elektroeintrag[];
}

export const ELEKTRO_ABSCHNITTE: Elektroabschnitt[] = [
  {
    name: 'Bestand',
    eintraege: [
      {
        pfad: 'elektro',
        name: 'Bauteile',
        hinweis: 'Der Bestand in vier Sichten: nach Anlage, Raum, '
          + 'Verteiler und Typennummer',
      },
      {
        pfad: 'elektro-geraete',
        name: 'Produktdaten',
        hinweis: 'Fabrikate und Kennwerte — dieselbe Detailseite wie bei '
          + 'Heizung, Lüftung und Sanitär',
      },
      {
        pfad: 'elektro-datenpflege',
        name: 'Datenpflege',
        hinweis: 'Felder füllen und ins Modell zurückschreiben',
      },
    ],
  },
  {
    name: 'Netz und Verteilung',
    eintraege: [
      {
        pfad: 'elektro-verteiler',
        name: 'Verteiler und Leistungsbilanz',
        hinweis: 'Verteiler, Abgänge, Stromkreise und die Bilanz '
          + 'UV → NSHV → Trafo → MS',
      },
      {
        pfad: 'elektro-verteilerplanung',
        name: 'Verteilerplanung',
        hinweis: 'Was die Bestückung aus dem Bestand gebildet hat: Kreise, '
          + 'Auslastung, Reserve — und der Verteilerplan ELT-VTP',
      },
      {
        // W2-B2 (16.10.2026): Küchen, Aufzüge, Fahrtreppen, Löschwasser,
        // Entrauchung und Pumpen der Fachplaner-Bilanz gegen das Modell.
        pfad: 'elektro-verbraucher',
        name: 'Verbraucherschnittstellen',
        hinweis: 'Küchen, Aufzüge, Löschwasser, Entrauchung: Strom, Anlauf, '
          + 'SV-Pflicht — und was davon im Modell noch fehlt',
      },
      {
        // W2-B1 (16.10.2026): Anschlusspunkte der FLB 8.8 (Powerlock, CEE)
        // gegen Modell, Netzberechnung, Fachplaner-Bilanz und SIMARIS.
        pfad: 'elektro-eventstrom',
        name: 'Eventstromversorgung',
        hinweis: 'Powerlock und CEE je Bühnenort: Leistung, Zuleitung, '
          + 'Selektivität, Messung — und was im Modell fehlt',
      },
      {
        // Auftrag Fachplaner 18.09.2026: IT-Verteiler aus der SV/UV-Ansicht
        // herausgelöst — ein LAN-Verteiler trägt kein Betriebsmittel aus der
        // Stromverteilung, sondern Patchfelder, Switches und Höheneinheiten.
        pfad: 'elektro-it-verteiler',
        name: 'IT-Verteiler',
        hinweis: '19"-Schrankaufbau: Bestückung je Höheneinheit und die '
          + 'Netzabgänge nach Art — getrennt von SV und UV',
      },
      {
        // J16 (13.10.2026): bisher nur API, PDF und Werkzeug.
        pfad: 'elektro-nea',
        name: 'Netzersatzanlagen',
        hinweis: 'Grundlagenmappe EG4: Datenblatt, Tank, NEA-Bilanz mit beiden '
          + 'Auslastungen, Abgleich mit dem Modell — nur Ansicht',
      },
      {
        // W2-A3 (16.10.2026): PV 1.000 kWp laut FLB 8.16.
        pfad: 'elektro-pv',
        name: 'PV-Anlage',
        hinweis: 'Wo speist die PV ein, trägt der Trafo die Rückspeisung, und '
          + 'was fehlt noch bis zur Auslegung — Spannen aus der FLB',
      },
      {
        pfad: 'elektro-simaris',
        name: 'SIMARIS',
        hinweis: 'Kurzschluss und Selektivität laut SIMARIS gegen das Modell '
          + 'von heute — nur Ansicht',
      },
    ],
  },
  {
    // W2-C1/W2-C2 (16.10.2026): Blitzschutz, Erdung, Ueberspannung und
    // Potentialausgleich - KG 446 und der Schutz der Niederspannungsanlage.
    name: 'Blitzschutz und Erdung',
    eintraege: [
      {
        pfad: 'elektro-blitzschutz',
        name: 'Blitzschutz, Erdung und Überspannung',
        hinweis: 'Liegen die Dachaufbauten im Schutzraum der Blitzkugel, reichen '
          + 'Ableitungen und Erder, und sitzt der Überspannungsschutz richtig',
      },
    ],
  },
  {
    name: 'Trassen und Kabel',
    eintraege: [
      {
        // EK (16.10.2026): Kabeldimensionierung nach Iz, Spannungsfall ab
        // Ursprung, Abschaltbedingung und Kurzschlussfestigkeit.
        pfad: 'elektro-kabelliste',
        name: 'Kabelliste und Kabeldimensionierung',
        hinweis: 'Kabeltyp, Querschnitt und PE je Zuleitung und Endstromkreis, '
          + 'das maßgebende Kriterium und die Nachweise — nur Ansicht, Excel',
      },
      {
        pfad: 'elektro-kabeltrassen',
        name: 'Kabeltrassen',
        hinweis: 'Nach Stromart gegliedert: was liegt zusammen in einer '
          + 'Trasse, und darf es das',
      },
      {
        // W2-C3 (16.10.2026): MLAR-Matrix, SV-Kreise, E30-/E90-Trassen.
        pfad: 'elektro-funktionserhalt',
        name: 'Funktionserhalt-Systemnachweis',
        hinweis: 'Hat jede Sicherheitsanlage den Funktionserhalt, den die MLAR '
          + 'verlangt, und tragen die Trassen das',
      },
      {
        // EK-Folgepaket (17.10.2026): DIN VDE 0100-718, Bereiche ueber Muster (ELT-38).
        pfad: 'elektro-versammlungsstaette',
        name: 'Versammlungsstätte (VDE 0100-718)',
        hinweis: 'RCD an Steckdosen, Beleuchtung auf mehreren Kreisen in Räumen für '
          + 'Besucher, Leitungen in Rettungswegen',
      },
    ],
  },
  {
    // Beleuchtung ist selbst schon ein kleines Gewerk: Nachweis der
    // Sehaufgabe, Nachweis des Rettungswegs, und mit H2/H4 kommen
    // Lichtsteuerung und der punktweise Nachweis dazu. Deshalb ein eigener
    // Abschnitt und nicht drei Einzelknöpfe irgendwo dazwischen.
    name: 'Beleuchtung',
    eintraege: [
      {
        pfad: 'elektro-beleuchtung',
        name: 'Nachweis je Raum',
        hinweis: 'DIN EN 12464-1 — reicht das Licht für die Tätigkeit, '
          + 'die in diesem Raum stattfindet',
      },
      {
        pfad: 'elektro-beleuchtung-punktverfahren',
        name: 'Punktverfahren (Ē, Emin, U0)',
        hinweis: 'Dasselbe in Punkten statt im Mittel: wo im Raum das '
          + 'Licht abfällt — als Näherung ausgewiesen',
      },
      {
        pfad: 'elektro-sicherheitsbeleuchtung',
        name: 'Sicherheitsbeleuchtung',
        hinweis: 'DIN EN 1838 — kommt man im Notfall heraus, und sieht '
          + 'man dabei den Weg',
      },
      {
        // W2-A2 (16.10.2026): Batterie, Ladung, Lüftung, Spannungsfall, Erkennungsweite.
        pfad: 'elektro-sicherheitsstrom',
        name: 'Sicherheitsstrom (ZSV/CPS)',
        hinweis: 'Reicht die Zentralbatterie für die Betriebsdauer, lädt sie '
          + 'rechtzeitig nach, und kommt am Ende noch Spannung an',
      },
      {
        pfad: 'elektro-dali',
        name: 'DALI-Linien und Lichtszenen',
        hinweis: 'Wer schaltet was: Busadressen, Gruppen und die Szenen '
          + 'je Nutzungsart',
      },
    ],
  },
  {
    // BM-1 (Eigentümer 20.10.2026): ein eigener Abschnitt Brandmeldeanlage. Die
    // bisherigen Seiten bleiben unter ihren Pfaden, neu ist die Übersicht mit
    // Gesamtbericht und Kapitelwahl.
    name: 'Brandmeldeanlage',
    eintraege: [
      {
        pfad: 'elektro-bma',
        name: 'Übersicht Brandmeldeanlage',
        hinweis: 'Kennzahlen, Vorbehalte, Kapitel und Befunde der BMA — '
          + 'Gesamtbericht und Kapitelwahl als PDF, Excel',
      },
      {
        pfad: 'elektro-melderbedarf',
        name: 'Melderbedarf und Auslegung',
        hinweis: 'Wie viele Melder braucht der Raum — bei TT-Platten '
          + 'entscheiden das die Unterzüge',
      },
      {
        pfad: 'elektro-meldelinien',
        name: 'Zentralen, Meldergruppen und Ringe',
        hinweis: 'Melder hängen an der Brandmeldezentrale, nicht an der '
          + 'Unterverteilung des Raums — Loopplan und Melderliste',
      },
      {
        // W2-D1 (16.10.2026): Steuermatrix, Notstrom, Ring- und RWA-Nachweis.
        pfad: 'elektro-sicherheitstechnik',
        name: 'Brandfallsteuerung und Notstrom',
        hinweis: 'Welche Meldung steuert was an, reicht die Batterie der '
          + 'BMZ und RWA, und hält der Ring die Spannung',
      },
    ],
  },
  {
    // BM-1 (E2, 20.10.2026): Sprachalarm und Zutritt mit eigener Route; bisher
    // nur als Reiter der Seite Brandfallsteuerung und Notstrom erreichbar.
    name: 'Sprachalarm und Zutritt',
    eintraege: [
      {
        pfad: 'elektro-sprachalarm',
        name: 'Sprachalarmanlage',
        hinweis: 'Linien, Verstärker, Zonen und Notstrom der SAA',
      },
      {
        pfad: 'elektro-zutritt',
        name: 'Zutritt, Fluchttür, Türüberwachung',
        hinweis: 'Türabgleich gegen das Modell und die Versorgung',
      },
    ],
  },
  {
    // CCTV Etappe 2 (Migration 0825, docs/CCTV.md): ein eigener Abschnitt —
    // Kameras hängen am IT-Schrank, nicht an einer Melderzentrale.
    name: 'Video / CCTV',
    eintraege: [
      {
        pfad: 'elektro-cctv',
        name: 'Kameras',
        hinweis: 'Kameras mit fester Kennung, IT-Schrank, Patchfeld, Switch, '
          + 'Kabellänge, PoE und Befunden — nur technische Planungsdaten',
      },
    ],
  },
];
